import { ChangeDetectorRef, Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TabViewModule } from 'primeng/tabview';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { MITableComponent } from 'app/modules/shared/components/mi-table/mi-table.component';
import { CommonBindingDataService } from 'app/modules/shared/services/common-binding-data.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { StorageService } from 'app/modules/shared/services/storage.service';
import { AppSettings } from 'app/modules/shared/app.settings';
import { AppIcons } from 'app/modules/shared/app.icons';
import { Country } from 'app/modules/shared/models/country';
import { Language } from 'app/modules/shared/models/language';
import { EntityList } from 'app/modules/shared/models/entity.models';
import { ListResponse } from 'app/modules/shared/models/listResponse.models';
import { MessageService, ConfirmationService } from 'primeng/api';
import * as _ from 'lodash';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-site-protocols',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    TabViewModule,
    BreadcrumbModule,
    AccessProviderDirective,
    MITableComponent
  ],
  templateUrl: './site-protocols.component.html',
  styleUrl: './site-protocols.component.scss'
})
export class SiteProtocolsComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  @Input() isActive: boolean = false;
  @Input() isSiteReady: boolean = false;
  @Input() siteEntityId: string;
  @Output() countUpdate = new EventEmitter<number>();
  @Output() saveSuccess = new EventEmitter<void>();

  miIcons = AppIcons;
  isLoading = true;
  filterAvailableColumns = [];
  cols = [];
  attributeLabels: Record<string, string> = {};
  filterAttributeLabels: Record<string, string>;
  listColumns: any;
  allColumns: any;
  pagination: any;
  totalRecords: number = 0;
  tableData: any[] = [];
  country: Country;
  language: Language;
  entityData: any;
  filterFieldsArray: any[];
  isEntity: boolean = false;
  filterList: any[];
  data: any;

  constructor(
    private cs: CommonBindingDataService,
    private entityService: EntityService,
    private loaderService: LoaderService,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService,
    private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private storageService: StorageService
  ) { }

  ngOnInit() {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.initEntityPayload();
    this.getMetaData();
    this.loaderService.hide();
  }

  ngOnChanges() {
    if (this.isActive && this.isSiteReady && this.siteEntityId) {
      this.getData();
    }
  }

  initEntityPayload() {
    this.entityData = {
      limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
      offset: 0,
      searchStr: "",
      eventCode: "",
      forTenantCode: this.configService.getForTenantCode(),
      viewCode: AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW,
      siteId: this.siteEntityId
    };
  }

  getMetaData() {
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res) => {
      const advancedFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advancedFilter) {
        this.setProtocolFilterView(advancedFilter);
      }

      const sort = JSON.parse(res.sort.responseBody);

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setProtocolTableView(defaultTableView);
      }
      this.cs.applyFilterToColumn(defaultTableView, sort);
    });
  }

  private getMultiApiPayload() {
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.PROTOCOL_LIST_SITE_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      }
    ];
  }

  setProtocolTableView(res: any) {
    this.isLoading = false;
    this.data = res;
    this.attributeLabels = this.cs.getAttributeLabels(res) as unknown as Record<string, string>;
    this.allColumns = this.cs.getColumns(res).filter(column =>
      !['entity_created_at'].includes(column.attributeCode)
    );

    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `protocol.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
    });
    this.filterAvailableColumns = _.clone(this.allColumns);

    if (this.isActive && this.isSiteReady && this.siteEntityId) {
      this.searchEntity();
    }
  }

  setProtocolFilterView(filterResponse: any) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse) as unknown as Record<string, string>;
    this.filterList = [];
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
  }

  getData() {
    if (this.allColumns && this.allColumns.length > 0) {
      this.isLoading = true;
      this.loaderService.hide();
      this.searchEntity();
      this.filterAvailableColumns = _.clone(this.allColumns);
    }
  }

  searchEntity() {
    this.loaderService.hide();

    // Update siteId if it changed
    if (this.siteEntityId) {
      this.entityData.siteId = this.siteEntityId;
    }

    this.entityService.getSiteProtocols(this.entityData, false)
      .subscribe({
        next: (res: ListResponse) => {
          this.listColumns = res?.data[0]?.values;
          this.pagination = res?.pagination;
          this.totalRecords = res?.count;
          this.tableData = [];
          res.data?.forEach(data => {
            this.tableData.push({
              id: data.id,
              actionStatus: data.values?.actionStatus || '',
              ...data.values,
            });
          });
          this.countUpdate.emit(this.totalRecords);
          this.loaderService.hide();
        },
        error: () => {
          this.tableData = [];
          this.totalRecords = 0;
          this.countUpdate.emit(0);
          this.loaderService.hide();
        }
      });
  }

  onPageChange(event: any) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  onSearchValueChanges(event: string) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  onFilterValueChange(event: any) {
    // Handle filter changes - filters are handled differently with custom API
    // Reset offset for new search
    this.entityData.offset = 0;

    // The custom API handles filtering internally based on siteId
    // Additional filters can be added to the payload if needed
    this.searchEntity();
  }

  itemPerPageChange(event: number) {
    this.entityData.limit = event;
    this.searchEntity();
  }

  onSortData(event: any) {
    this.entityData.defaultSortColumn = event.attributeId;
    this.entityData.defaultSortType = (event.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  onRowSelect(event: any) {
    if (event.type === 'row') {
      this.activatedRoute.queryParams.subscribe(params => {
        const fromTab = params['fromTab'];
        const isFromTrials = fromTab && ['all', 'draft', 'active', 'completed', 'inactive'].includes(fromTab);

        this.router.navigate(['app/trials/trial-details'], {
          queryParams: {
            tab: 'overview',
            id: event.data.id,
            fromTab: isFromTrials ? fromTab : 'site-protocols'
          }
        });
      });

      this.entityService.entityCurrentRow.next(event.data);
    }
  }

  ngOnDestroy() {
    this.loaderService.hide();
  }
}